/* The phone app's furniture, and the way it writes numbers.
 *
 * Every screen is built from the same handful of pieces the design uses:
 * a two-up KPI grid, a bar chart with an optional second series as a line,
 * a small line chart with its own axis labels, a ranked bar list, a note
 * panel, the efficiency calendar and the day strip. They are here rather
 * than in components/ui.tsx because they are all sized for one column at
 * 390px, which is not a decision the desktop pages want.
 *
 * The charts are inline SVG and CSS boxes, as everywhere else in this app —
 * no chart library, no CDN, and they repaint with the theme because every
 * mark takes its colour from a ds.css token.
 */

import { type ReactNode, useEffect, useRef } from 'react'
import { Blueprint } from '../components/ui'

// ------------------------------------------------------------- formatting
/** Indian grouping — 1,23,456, not 123,456. The mill reads lakhs. */
export const IN = (n: number | null | undefined) =>
  n == null ? '–' : Math.round(n).toLocaleString('en-IN')
export const KG = (n: number | null | undefined) => (n == null ? '–' : `${IN(n)} kg`)
export const TN = (n: number | null | undefined) =>
  n == null ? '–' : `${(n / 1000).toFixed(1)} t`
export const CR = (v: number | null | undefined) =>
  v == null ? '–' : `₹${(v / 1e7).toFixed(2)} Cr`
export const LK = (v: number | null | undefined) =>
  v == null ? '–' : `₹${(v / 1e5).toFixed(1)} L`
export const P1 = (v: number | null | undefined) =>
  v == null ? '–' : `${v.toFixed(1)}%`
export const RS = (v: number | null | undefined) => (v == null ? '–' : `₹${IN(v)}`)

/** A signed figure. U+2212 for the minus — a hyphen is narrower than the
 *  plus it sits under in a tabular column. */
export const SG = (n: number | null | undefined, f: (x: number) => string) =>
  n == null ? '–' : (n >= 0 ? '+' : '−') + f(Math.abs(n))

export const BAD = 'var(--c-bad)'
export const INK = 'var(--color-text)'

// -------------------------------------------------------------------- kpis
export interface MKpi { label: string; value: string; sub: string; bad?: boolean }

export const kpi = (label: string, value: string, sub: string, bad = false): MKpi =>
  ({ label, value, sub, bad })

export function KpiGrid({ items, style }: { items: MKpi[]; style?: React.CSSProperties }) {
  return (
    <div className="m-grid-2" style={{ marginTop: 20, ...style }}>
      {items.map((k) => (
        // Blueprint's own padding is set inline for the desktop pages, so it
        // has to be overridden the same way rather than from the class.
        <Blueprint key={k.label} className="m-kpi" style={{ padding: '12px 12px 14px' }}>
          <div className="m-label" style={{ color: 'var(--color-accent-700)' }}>{k.label}</div>
          <div className="m-kpi-value" style={{ color: k.bad ? BAD : INK }}>{k.value}</div>
          <div className="m-kpi-sub">{k.sub}</div>
        </Blueprint>
      ))}
    </div>
  )
}

/** A blueprint panel that is itself the tap target — a tile, or the sign-in
 *  button. `Blueprint` is a div, and nesting a button inside a tile would
 *  leave the panel's own padding untappable. */
export function BpButton({ children, onClick, className = '', style, title }: {
  children: ReactNode
  onClick: () => void
  className?: string
  style?: React.CSSProperties
  title?: string
}) {
  return (
    <button type="button" onClick={onClick} title={title}
      className={`blueprint ${className}`}
      style={{ position: 'relative', ...style }}>
      <i className="corner tl" /><i className="corner tr" />
      <i className="corner bl" /><i className="corner br" />
      {children}
    </button>
  )
}

// --------------------------------------------------------------- headings
export function Head({ title, aside }: { title: string; aside?: ReactNode }) {
  return (
    <div className="m-head">
      <h3 className="m-h3">{title}</h3>
      {aside ? <span className="m-aside" style={{ whiteSpace: 'nowrap' }}>{aside}</span> : null}
    </div>
  )
}

export function NoteBox({ children }: { children: ReactNode }) {
  return (
    <Blueprint style={{
      marginTop: 18, padding: '12px 14px', fontSize: 12.5, lineHeight: 1.5,
      color: 'var(--color-neutral-800)',
    }}>{children}</Blueprint>
  )
}

/** The two-up picker that heads most screens (Daily / Month, Yarn / Waste). */
export function Seg<T extends string>({ value, options, onChange, style }: {
  value: T
  options: { v: T; l: string }[]
  onChange: (v: T) => void
  style?: React.CSSProperties
}) {
  return (
    <div className="m-seg" style={{ marginTop: 10, ...style }}>
      {options.map((o) => (
        <button key={o.v} type="button" aria-pressed={value === o.v}
          onClick={() => onChange(o.v)}>{o.l}</button>
      ))}
    </div>
  )
}

export function Legend({ items }: {
  items: { t: string; c?: string; border?: string; line?: boolean }[]
}) {
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 10,
      fontSize: 11, color: 'var(--color-neutral-700)',
    }}>
      {items.map((l) => (
        <span key={l.t} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            width: l.line ? 16 : 12,
            height: l.line ? 0 : 10,
            background: l.c ?? 'transparent',
            border: l.border,
            borderTop: l.line ? l.border : undefined,
            boxSizing: 'border-box',
          }} />
          {l.t}
        </span>
      ))}
    </div>
  )
}

// -------------------------------------------------------------- bar chart
export interface MBar {
  /** 0–100, as a percentage of the plot height. */
  h: number
  /** The target outline behind the bar, same scale; 0 for none. */
  th?: number
  c?: string
  /** Marks the bar the screen is showing. */
  sel?: boolean
  /** Printed above the bar. */
  top?: string
  /** The axis label under it; '' leaves the slot blank. */
  lab?: string
  /** A second figure under the label, in the heading face. */
  sub?: string
  on?: () => void
}

export function Bars({ bars, gap = 2, refLine, line, legend }: {
  bars: MBar[]
  gap?: number
  /** A dashed rule across the plot at this height, 0–100. */
  refLine?: number
  /** A second series as a line over the bars, each point 0–100. */
  line?: { points: number[]; label?: string }
  legend?: { t: string; c?: string; border?: string; line?: boolean }[]
}) {
  const pts = line
    ? line.points.map((p, i) => `${((i + 0.5) / line.points.length * 100).toFixed(2)},${(100 - p).toFixed(2)}`).join(' ')
    : ''
  return (
    <>
      <div style={{
        position: 'relative', height: 140, display: 'flex', alignItems: 'flex-end',
        gap, borderBottom: '1px solid var(--color-neutral-500)',
      }}>
        {bars.map((b, i) => (
          <div
            key={i}
            onClick={b.on}
            role={b.on ? 'button' : undefined}
            style={{
              flex: 1, minWidth: 0, height: '100%', position: 'relative',
              cursor: b.on ? 'pointer' : 'default',
            }}
          >
            {b.th ? (
              <div style={{
                position: 'absolute', left: 0, right: 0, bottom: 0, height: `${b.th}%`,
                border: '1px solid var(--m-target)', borderBottom: 0, boxSizing: 'border-box',
              }} />
            ) : null}
            <div style={{
              position: 'absolute', left: 0, right: 0, bottom: 0, height: `${b.h}%`,
              background: b.c ?? 'var(--c-bar1)',
              outline: b.sel ? '2px solid var(--color-accent)' : undefined,
              outlineOffset: 1,
            }} />
            {b.top ? (
              <div className="m-num" style={{
                position: 'absolute', left: -10, right: -10, bottom: `${b.h}%`,
                marginBottom: 3, textAlign: 'center', fontSize: 10,
                color: 'var(--color-neutral-700)',
              }}>{b.top}</div>
            ) : null}
          </div>
        ))}
        {refLine != null && (
          <div style={{
            position: 'absolute', left: 0, right: 0, bottom: `${refLine}%`,
            borderTop: '1px dashed var(--c-ink)', opacity: 0.6, pointerEvents: 'none',
          }} />
        )}
        {line && (
          <>
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              overflow: 'visible', pointerEvents: 'none',
            }}>
              <polyline points={pts} fill="none" strokeWidth={2}
                vectorEffect="non-scaling-stroke" style={{ stroke: 'var(--c-good)' }} />
            </svg>
            {line.points.map((p, i) => (
              <span key={i} style={{
                position: 'absolute',
                left: `${(i + 0.5) / line.points.length * 100}%`,
                bottom: `${p}%`,
                width: 8, height: 8, margin: '0 0 -4px -4px',
                background: 'var(--color-bg)', border: '2px solid var(--c-good)',
                boxSizing: 'border-box', pointerEvents: 'none',
              }} />
            ))}
            {line.label ? (
              <div className="m-num" style={{
                position: 'absolute', right: -2, top: -16, fontSize: 10,
                color: 'var(--c-good)',
              }}>{line.label}</div>
            ) : null}
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap, marginTop: 5 }}>
        {bars.map((b, i) => (
          <div key={i} style={{
            flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', gap: 3, fontSize: 10,
            color: 'var(--color-neutral-600)', whiteSpace: 'nowrap',
          }}>
            <span>{b.lab ?? ''}</span>
            {b.sub ? (
              <span className="m-num" style={{
                fontFamily: 'var(--font-heading)', fontSize: 13, fontWeight: 600,
                color: 'var(--color-text)',
              }}>{b.sub}</span>
            ) : null}
          </div>
        ))}
      </div>
      {legend ? <Legend items={legend} /> : null}
    </>
  )
}

// ------------------------------------------------------------- line chart
/** A series plotted in a 300x120 box with its three axis labels beside it.
 *  The band is given rather than computed from the data so a stock chart
 *  can keep a little air above and below its range. */
export function LineChart({ series, lo, hi, fmt, from, to, dashed, legend }: {
  series: number[]
  lo: number
  hi: number
  fmt: (v: number) => string
  from: string
  to: string
  /** A second, dashed series on the same band — a comparison or a target. */
  dashed?: number[]
  legend?: { t: string; c?: string; border?: string; line?: boolean }[]
}) {
  const span = hi - lo || 1
  const path = (s: number[]) => s
    .map((v, i) => `${(i / Math.max(1, s.length - 1) * 300).toFixed(1)},${(110 - (v - lo) / span * 100).toFixed(1)}`)
    .join(' ')
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 6 }}>
        <svg viewBox="0 0 300 120" preserveAspectRatio="none" style={{
          width: '100%', height: 130, display: 'block',
          borderLeft: '1px solid var(--color-neutral-500)',
          borderBottom: '1px solid var(--color-neutral-500)',
        }}>
          <line x1="0" y1="10" x2="300" y2="10" strokeWidth={1}
            vectorEffect="non-scaling-stroke" style={{ stroke: 'var(--c-grid)' }} />
          <line x1="0" y1="60" x2="300" y2="60" strokeWidth={1}
            vectorEffect="non-scaling-stroke" style={{ stroke: 'var(--c-grid)' }} />
          {dashed && dashed.length > 1 && (
            <polyline points={path(dashed)} fill="none" strokeWidth={1.4}
              strokeDasharray="4 3" vectorEffect="non-scaling-stroke"
              style={{ stroke: 'var(--c-bar1)' }} />
          )}
          <polyline points={path(series)} fill="none" strokeWidth={1.8}
            vectorEffect="non-scaling-stroke" style={{ stroke: 'var(--c-ink)' }} />
        </svg>
        <div className="m-num" style={{
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          height: 130, padding: '2px 0 0', fontSize: 10, color: 'var(--color-neutral-600)',
        }}>
          <span>{fmt(hi)}</span><span>{fmt((hi + lo) / 2)}</span><span>{fmt(lo)}</span>
        </div>
      </div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', marginTop: 5,
        paddingRight: 36, fontSize: 10, color: 'var(--color-neutral-600)',
      }}><span>{from}</span><span>{to}</span></div>
      {legend ? <Legend items={legend} /> : null}
    </>
  )
}

/** A sparkline for a dashboard tile: 100x24, no axes, no labels. */
export function Spark({ series }: { series: number[] }) {
  if (series.length < 2) return <div style={{ height: 22 }} />
  const lo = Math.min(...series)
  const hi = Math.max(...series)
  const span = hi - lo || 1
  const pts = series
    .map((v, i) => `${(i / (series.length - 1) * 100).toFixed(1)},${(22 - (v - lo) / span * 20).toFixed(1)}`)
    .join(' ')
  return (
    <svg viewBox="0 0 100 24" preserveAspectRatio="none" style={{
      width: '100%', height: 22, display: 'block', marginTop: 'auto',
    }}>
      <polyline points={pts} fill="none" strokeWidth={1.5}
        vectorEffect="non-scaling-stroke" style={{ stroke: 'var(--c-bar1)' }} />
    </svg>
  )
}

// ------------------------------------------------------------ ranked list
export function Rank({ items }: { items: { l: string; v: string; w: number }[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((r) => (
        <div key={r.l} style={{
          display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto',
          gap: '4px 10px', alignItems: 'center',
        }}>
          <span className="m-ellipsis" style={{ fontSize: 12.5 }}>{r.l}</span>
          <span className="m-num" style={{ fontSize: 12, color: 'var(--color-neutral-700)' }}>{r.v}</span>
          <div style={{ gridColumn: '1 / -1', height: 8, background: 'var(--c-track)' }}>
            <div style={{ height: '100%', width: `${r.w}%`, background: 'var(--c-bar1)' }} />
          </div>
        </div>
      ))}
    </div>
  )
}

// ----------------------------------------------------------------- heat
/** The calendar's and the day strip's colour ramp.
 *
 * Two ramps meeting at the benchmark, so "below bench" is a different
 * family of colours rather than a darker shade of the same one — the
 * distinction a glance has to make. Dark mode inverts: a light cell is the
 * bad one, because on a navy ground light reads as the loud end.
 */
const hex = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16))
const mix = (a: string, b: string, t: number) => {
  const k = Math.max(0, Math.min(1, t))
  const A = hex(a)
  const B = hex(b)
  return '#' + A.map((v, i) => Math.round(v + (B[i] - v) * k).toString(16).padStart(2, '0')).join('')
}

export function heat(v: number, lo: number, hi: number, bench: number, dark: boolean) {
  if (v < bench) {
    const t = (bench - v) / ((bench - lo) || 1)
    return dark
      ? { bg: mix('#8fb4ff', '#e8ecf5', t), fg: '#0f1626' }
      : { bg: mix('#416180', '#1d2d3d', t), fg: '#ffffff' }
  }
  const t = (v - bench) / ((hi - bench) || 1)
  return dark
    ? { bg: mix('#1c2743', '#2c4a8a', t), fg: '#e8ecf5' }
    : { bg: mix('#d6ebff', '#94bce3', t), fg: '#1d2d3d' }
}

export const legendColours = (dark: boolean) => ({
  hi: dark ? '#2c4a8a' : '#94bce3',
  lo: dark ? '#e8ecf5' : '#1d2d3d',
})

// ------------------------------------------------------------- calendar
export interface CalDay { d: number; v: number; dow: string }

/** The month as a 7-column grid, each cell its day's efficiency.
 *
 *  The leading blanks come from the first day's weekday, so the columns
 *  really are Monday to Sunday — the design hard-coded one blank because
 *  its sample month happened to start on a Tuesday.
 */
export function Calendar({ days, bench, dark, selected, onPick }: {
  days: CalDay[]
  bench: number
  dark: boolean
  selected?: number | null
  onPick?: (i: number) => void
}) {
  if (!days.length) return null
  const vals = days.map((d) => d.v)
  const lo = Math.min(...vals)
  const hi = Math.max(...vals)
  const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const lead = Math.max(0, DOW.indexOf(days[0].dow))

  return (
    <>
      <div className="m-cal" style={{ marginTop: 4 }}>
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((w, i) => (
          <div key={i} style={{
            fontSize: 10, textAlign: 'center', color: 'var(--color-neutral-600)',
          }}>{w}</div>
        ))}
        {Array.from({ length: lead }, (_, i) => <div key={`b${i}`} />)}
        {days.map((day, i) => {
          const h = heat(day.v, lo, hi, bench, dark)
          return (
            <button
              key={day.d}
              type="button"
              onClick={onPick ? () => onPick(i) : undefined}
              aria-current={selected === i ? 'true' : undefined}
              style={{
                background: h.bg, color: h.fg,
                outline: selected === i ? '2px solid var(--color-text)' : 'none',
                cursor: onPick ? 'pointer' : 'default',
              }}
            >
              <span style={{ fontSize: 9, opacity: 0.85 }}>{day.d}</span>
              <span className="m-num" style={{ fontSize: 11.5, fontWeight: 600 }}>
                {day.v.toFixed(1)}
              </span>
            </button>
          )
        })}
      </div>
      <div style={{
        display: 'flex', gap: 14, marginTop: 10, fontSize: 11,
        color: 'var(--color-neutral-700)',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 12, height: 10, background: legendColours(dark).hi }} />
          at or above {bench}%
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 12, height: 10, background: legendColours(dark).lo }} />
          below {bench}%
        </span>
      </div>
    </>
  )
}

// ------------------------------------------------------------- day strip
/** The month as a horizontal strip of day tiles, the chosen one scrolled
 *  into the middle whenever it changes — so tapping a day on the calendar
 *  and landing here does not leave the day off-screen. */
export function DayStrip({ days, bench, dark, selected, onPick }: {
  days: CalDay[]
  bench: number
  dark: boolean
  selected: number
  onPick: (i: number) => void
}) {
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = box.current
    const sel = el?.querySelector<HTMLElement>('[data-sel="1"]')
    if (el && sel) el.scrollLeft = sel.offsetLeft - el.clientWidth / 2 + sel.offsetWidth / 2
  }, [selected])

  const vals = days.map((d) => d.v)
  const lo = Math.min(...vals)
  const hi = Math.max(...vals)

  return (
    <div ref={box} className="m-rail m-noscroll"
      style={{ gap: 4, marginTop: 14, paddingTop: 2, paddingBottom: 4 }}>
      {days.map((day, i) => {
        const h = heat(day.v, lo, hi, bench, dark)
        return (
          <button
            key={day.d}
            type="button"
            className="m-day"
            data-sel={i === selected ? '1' : '0'}
            onClick={() => onPick(i)}
            style={{
              background: h.bg, color: h.fg,
              outline: i === selected ? '2px solid var(--color-accent)' : 'none',
            }}
          >
            <span style={{ fontSize: 9, letterSpacing: '0.06em', opacity: 0.85 }}>
              {day.dow}
            </span>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: 17, lineHeight: 1 }}>
              {day.d}
            </span>
            <span style={{ fontSize: 10, fontWeight: 600 }}>{day.v.toFixed(1)}</span>
          </button>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------- icons
export const Hamburger = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
    <path d="M4 6h16" /><path d="M4 12h16" /><path d="M4 18h16" />
  </svg>
)

export const Cross = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
    <path d="M18 6 6 18" /><path d="m6 6 12 12" />
  </svg>
)
